import { Button, Checkbox, Popover, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import Avatar from 'components/Avatar';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import FilterList from 'components/FilterList';
import type { OptionMeta } from 'components/FilterListItem';
import type { TeamFilterDropdownOption } from 'factories/teams';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import type { Team } from 'types/team';
import type { UnionToIntersection } from 'types/utility';
import type { FilterListItem } from 'utils/filter-list';
import { countableLabel } from 'utils/text-helper';
import styles from './TeamFilterDropdown.module.scss';

type TeamOrTeamOption = Team | TeamFilterDropdownOption;
type TeamOrTeamOptionIntersection = UnionToIntersection<TeamOrTeamOption>;
interface Props {
  teams: Array<TeamOrTeamOption>;
  onChange: (ids: Array<number>) => void;
  singleSelect?: boolean;
  selected: Array<number>;
  emptyLabel?: string;
  highlightModified?: boolean;
  disabled?: boolean;
  compact?: boolean;
}
const TeamFilterDropdown = (props: Props) => {
  const {
    teams = [],
    emptyLabel = 'No team',
    highlightModified = true,
    singleSelect,
    onChange,
    compact,
    disabled,
    selected = [],
  } = props;

  const [groupFilter, setGroupFilter] = React.useState<string | null>(null);

  const handleOnChangeGroup = (groupName: string, teamIds: Array<number>) => {
    setGroupFilter(groupName);
    onChange(teamIds);
  };

  // Return a complete list if id's selected on each onChange
  const handleOnChange = (_: unknown, teamIdList: Array<number>) => {
    onChange(teamIdList);
  };

  const handleBatchSelect = (batchName: string, teamIds: Array<number>) => {
    onChange(teamIds);
  };

  // Support both "raw" team arrays, or pre-formatted options for FilterList
  const options = teams.map((team: TeamOrTeamOptionIntersection, index) => {
    return {
      sequence: team.sequence !== 0 ? team.sequence : index,
      value: team.id || team.value!,
      name: team.name!,
      image: team.emoji || team.image,
      groupFilter: team.groupFilter || team.deleted ? 'deleted' : 'active',
    };
  });

  const firstSelectedTeam =
    singleSelect && selected.length ? options.find(o => o.value === selected[0]) : null;

  const hasDeletedTeams = teams.filter(team => team.deleted).length;

  const groupFilters = hasDeletedTeams
    ? [
        { value: null, name: 'All Teams' },
        { value: 'active', name: 'Active' },
        { value: 'deleted', name: 'Deleted' },
      ]
    : false;

  const total = teams.length;
  const isSelected = selected.length > 0 && !singleSelect && highlightModified;
  const label = countableLabel('team', { selected: selected.length, total, compact });
  const singleSelectLabel = firstSelectedTeam?.name || emptyLabel;

  const renderTeamOption = (option: Partial<FilterListItem>, meta: OptionMeta) => {
    const showCheckbox = !singleSelect;

    return (
      <div className={classNames('fs-mask', styles.optionRow)}>
        {showCheckbox ? (
          <Checkbox tabIndex={-1} readOnly disabled={meta.isDisabled} checked={meta.isSelected} />
        ) : null}
        {option.image && <img src={option.image} className="fs-exclude" />}
        <DiagnosticResourceId resource="team" id={option.value} />
        <div className={styles.optionLabelWrapper}>
          <Tooltip
            tooltip={option.name || ''}
            usePortal={true}
            position="right"
            delay={250}
            childrenClassName={styles.optionLabel}
          >
            {option.name}
          </Tooltip>
        </div>
        {!!(meta.hint || meta.hintTooltip) && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '5px' }}>
            {meta.hint}
            {meta.hintTooltip}
          </span>
        )}
      </div>
    );
  };

  return (
    <Popover
      closeOnOutsideClick
      closeOnClick={singleSelect}
      fixed
      targetCaret
      target={
        <Button
          priority={isSelected ? 'low' : 'medium'}
          selected={isSelected}
          className={styles.trigger}
          disabled={disabled}
          data-testid="team-select-dropdown"
        >
          {singleSelect ? (
            <span>
              <Avatar size="small" src={firstSelectedTeam?.image} shape="circle" />
              &nbsp;
              {singleSelectLabel}
            </span>
          ) : (
            <span>
              <FontAwesome name="team" />
              <span>{label}</span>
            </span>
          )}
        </Button>
      }
      className={styles.modalContent}
    >
      <FilterList
        resource="team"
        ulClassName={styles.filterListUl}
        onChange={handleOnChange}
        onChangeGroup={handleOnChangeGroup}
        onBatchSelect={handleBatchSelect}
        values={selected}
        options={options}
        placeholder="Search teams"
        groupFilter={groupFilter}
        groupFilters={groupFilters}
        singleSelect={singleSelect}
        renderOption={renderTeamOption}
      />
    </Popover>
  );
};

export default TeamFilterDropdown;
